/**
 * Module dependencies.
 */

import { ErrorField } from '../../error-field/error-field';
import { FormField } from '../../form-field/form-field';
import { Input } from 'app/components/ui/input';
import { Slider as SliderPrimitive } from 'app/components/ui/slider';
import { cn } from 'app/lib/utils';
import React, { forwardRef, useMemo } from 'react';

/**
 * `Props` type.
 */

export type SliderProps = {
  className?: string;
  disabled?: boolean;
  error: any;
  fieldClassName?: string;
  invalid?: boolean;
  isDirty?: boolean;
  isTouched?: boolean;
  label: string;
  max?: number;
  min?: number;
  name?: string;
  onBlur?: () => void;
  onChange?: (value: number) => void;
  step?: number;
  value?: number;
};

/**
 * Export `SliderComponent` component.
 */

export function SliderComponent(props: SliderProps, forwardedRef: any): JSX.Element {
  const {
    className,
    disabled,
    error,
    fieldClassName,
    invalid,
    isTouched,
    label,
    max,
    min,
    name,
    onBlur,
    onChange,
    step,
    value
  } = props;
  const normalizedValue = useMemo(() => {
    return typeof value === 'number' ? value : 0;
  }, [value]);

  return (
    <FormField className={className} label={label}>
      <div className={'flex items-center gap-2'}>
        <div className={'flex-[4]'}>
          {value !== undefined && (
            <SliderPrimitive
              disabled={disabled}
              max={max}
              min={min}
              onValueChange={values => onChange?.(values[0])}
              ref={forwardedRef}
              step={step}
              value={[normalizedValue]}
            />
          )}
        </div>

        <div className={'flex-[2]'}>
          <Input
            className={cn(fieldClassName)}
            disabled={disabled}
            max={max}
            min={min}
            name={name}
            onBlur={onBlur}
            onChange={event => onChange?.(event.target.value as any)}
            step={step}
            type={'number'}
            value={normalizedValue}
          />
        </div>
      </div>

      <ErrorField error={error} isVisible={invalid || isTouched} />
    </FormField>
  );
}

/**
 * Export `Slider` component.
 */

export const Slider = forwardRef<any, SliderProps>(SliderComponent);
